import type { FC, ReactNode } from 'react'
import type { Stage } from '@/constants/full-service'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { stages } from '@/constants/full-service'

const StageBlock: FC<{ stage: Stage, index: number, register: (element: HTMLElement | null) => void }> = ({ stage, index, register }): ReactNode => (
    <article
        ref={register}
        id={stage.id}
        aria-labelledby={`${stage.id}-title`}
        className='scroll-mt-10 border-b border-hairline py-20 last:border-b-0 md:py-28 lg:scroll-mt-0'
    >
        <Reveal>
            <p className='text-[13px] font-medium text-violet'>
                {String(index + 1).padStart(2, '0')}
                <span className='mx-2 text-[#c9c9d2]'>/</span>
                {stage.label}
            </p>

            <h3 id={`${stage.id}-title`} className='mt-5 max-w-[780px] text-[clamp(1.625rem,2.8vw,2.375rem)] font-medium leading-[1.2] tracking-[-0.03em] text-slate'>
                {stage.intro}
                <span className='text-ink'>{stage.emphasis}</span>
            </h3>
        </Reveal>

        <div className='mt-12 grid gap-10 md:mt-14 md:grid-cols-[240px_minmax(0,1fr)] md:gap-14'>
            <Reveal className='self-start md:sticky md:top-32'>
                <figure className='flex items-center gap-4 rounded-[20px] bg-mist p-4 ring-1 ring-inset ring-[#ececf1] md:flex-col md:items-start md:p-5'>
                    <Image
                        src={stage.person.image}
                        alt={stage.person.name}
                        width={88}
                        height={88}
                        className='size-16 shrink-0 rounded-full object-cover object-top ring-4 ring-white md:size-[88px]'
                    />

                    <figcaption className='flex flex-col gap-0.5'>
                        <span className='text-[16px] font-semibold tracking-[-0.01em] text-ink'>
                            {stage.person.name}
                        </span>

                        <span className='text-[14px] text-slate'>
                            {stage.person.role}
                        </span>
                    </figcaption>
                </figure>
            </Reveal>

            <ol className='flex flex-col border-t border-hairline'>
                {stage.steps.map((step, stepIndex) => (
                    <Reveal
                        as='li'
                        key={step.title}
                        delay={stepIndex * 60}
                        className='grid grid-cols-[56px_minmax(0,1fr)] gap-4 border-b border-hairline py-7 md:grid-cols-[72px_minmax(0,1fr)] md:py-8'
                    >
                        <span aria-hidden='true' className='text-[36px] font-medium leading-none tracking-[-0.04em] text-[#d0d0d8] tabular-nums md:text-[44px]'>
                            {String(stepIndex + 1).padStart(2, '0')}
                        </span>

                        <div className='flex flex-col gap-2 pt-1'>
                            <h4 className='text-[18px] font-semibold tracking-[-0.015em] text-ink'>
                                <span className='sr-only'>Step {stepIndex + 1}: </span>
                                {step.title}
                            </h4>

                            <p className='max-w-[560px] text-[15.5px] leading-relaxed text-slate'>
                                {step.description}
                            </p>
                        </div>
                    </Reveal>
                ))}
            </ol>
        </div>
    </article>
)

export const Stages: FC = (): ReactNode => {
    const [active, setActive] = useState(stages[0].id)
    const elements = useRef<Record<string, HTMLElement | null>>({})

    useEffect(() => {
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) setActive(entry.target.id)
            })
        }, { rootMargin: '-38% 0px -58% 0px' })

        Object.values(elements.current).forEach(element => {
            if (element) observer.observe(element)
        })

        return () => observer.disconnect()
    }, [])

    return (
        <section id='process' aria-labelledby='process-title' className='relative border-t border-hairline'>
            <h2 id='process-title' className='sr-only'>
                How we deliver every workflow
            </h2>

            <nav aria-label='Delivery stages' className='sticky top-16 z-20 border-b border-hairline bg-white/90 backdrop-blur-xl lg:hidden'>
                <div className='fc-container flex gap-6 overflow-x-auto no-scrollbar'>
                    {stages.map(stage => (
                        <a
                            key={stage.id}
                            href={`#${stage.id}`}
                            aria-current={active === stage.id ? 'step' : undefined}
                            className={cn(
                                'relative shrink-0 py-3.5 text-[15px] font-medium transition-colors',
                                active === stage.id ? 'text-ink' : 'text-[#9a9aa4]'
                            )}
                        >
                            {stage.label}

                            <span aria-hidden='true' className={cn('absolute inset-x-0 -bottom-px h-[2px] rounded-full bg-ink transition-opacity duration-300', active === stage.id ? 'opacity-100' : 'opacity-0')} />
                        </a>
                    ))}
                </div>
            </nav>

            <div className='fc-container grid lg:grid-cols-[200px_minmax(0,1fr)] lg:gap-16'>
                <div className='hidden lg:block'>
                    <nav aria-label='Delivery stages' className='sticky top-[136px] flex flex-col gap-1 pb-28 pt-28'>
                        {stages.map(stage => (
                            <a
                                key={stage.id}
                                href={`#${stage.id}`}
                                aria-current={active === stage.id ? 'step' : undefined}
                                className={cn(
                                    'relative rounded-md py-1.5 pl-5 text-[17px] font-medium tracking-[-0.01em] transition-colors duration-300 focus-visible:outline-2 focus-visible:outline-violet',
                                    active === stage.id ? 'text-ink' : 'text-[#a0a0aa] hover:text-graphite'
                                )}
                            >
                                <span
                                    aria-hidden='true'
                                    className={cn('absolute left-0 top-1/2 h-6 w-[2px] -translate-y-1/2 rounded-full bg-ink transition-[opacity,scale] duration-300', active === stage.id ? 'scale-100 opacity-100' : 'scale-50 opacity-0')}
                                />
                                {stage.label}
                            </a>
                        ))}
                    </nav>
                </div>

                <div className='lg:border-l lg:border-hairline lg:pl-16'>
                    {stages.map((stage, index) => (
                        <StageBlock
                            key={stage.id}
                            stage={stage}
                            index={index}
                            register={element => {
                                elements.current[stage.id] = element
                            }}
                        />
                    ))}
                </div>
            </div>
        </section>
    )
}
